import apiClient from '../services/apiClient';
import { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { FormInput, Button } from '../components/ui';

interface PasswordState {
  oldPassword: string;
  newPassword: string;
  confirmPassword: string;
}

interface ProfileState {
  username: string;
  email: string;
  real_name: string;
  phone_no: string;
  address: string;
}

const ChangePassword = () => {
  const [profile, setProfile] = useState<ProfileState>({
    username: '',
    email: '',
    real_name: '',
    phone_no: '',
    address: '',
  });

  const [password, setPassword] = useState<PasswordState>({
    oldPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const [profileLoading, setProfileLoading] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);

  // Fetch profile details on mount
  const fetchProfile = async () => {
    try {
      const res = await apiClient.get('/admin/getMineProfile');
      if (res.data?.success && res.data?.data) {
        const d = res.data.data;
        setProfile({
          username: d.username || '',
          email: d.email || '',
          real_name: d.real_name || '',
          phone_no: d.phone_no || '',
          address: d.address || '',
        });
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
    }
  };

  useEffect(() => {
    fetchProfile();
  }, []);

  // Handle profile text input changes
  const handleProfileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setProfile((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Handle password text input changes
  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setPassword((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Handle profile form submission
  const handleProfileSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const { username, email } = profile;

    if (!username || !email) {
      toast.error('Username and Email are required.');
      return;
    }

    setProfileLoading(true);
    try {
      const res = await apiClient.post('/admin/updateMineProfile', profile);
      if (res.data?.success) {
        toast.success(res.data.message || 'Profile updated successfully!');
        fetchProfile();
      } else {
        toast.error(res.data.message || 'Failed to update profile.');
      }
    } catch (error: any) {
      console.error('Error updating profile:', error);
      toast.error(error.response?.data?.message || 'Failed to update profile.');
    } finally {
      setProfileLoading(false);
    }
  };

  // Handle password form submission
  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const { oldPassword, newPassword, confirmPassword } = password;

    if (!oldPassword || !newPassword || !confirmPassword) {
      toast.error('All password fields are required.');
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error('New password and confirm password do not match.');
      return;
    }

    let token = localStorage.getItem("token");

    setPasswordLoading(true);
    try {
      const response = await apiClient.post(
        `/admin/changepassword`,
        { token, oldPassword, newPassword, confirmPassword },
        {
          headers: { 'Content-Type': 'application/json' },
        }
      );

      toast.success(response.data.message || 'Password changed successfully!');
      setPassword({
        oldPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
    } catch (error) {
      const err = error as any;
      console.error('Error updating password:', err);
      toast.error(
        err.response?.data?.message || 'Failed to update password. Please try again.'
      );
    } finally {
      setPasswordLoading(false);
    }
  };

  return (
    <div className='ml-0 lg:ml-[256px] p-4 sm:p-6 text-white min-h-screen'>
      <div className="max-w-6xl mx-auto space-y-6">
        <h2 className='text-xl sm:text-2xl font-bold text-white mb-2'>Account Settings</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Profile Details Panel */}
          <div className="bg-[#1E2437] p-6 rounded-xl border border-slate-800/80 shadow-lg flex flex-col justify-between">
            <div>
              <h3 className='text-lg font-bold mb-4 text-white border-b border-slate-850 pb-2'>Profile Details</h3>
              <form onSubmit={handleProfileSubmit} className='space-y-4'>
                <FormInput 
                  label="Username" 
                  type="text" 
                  name="username" 
                  value={profile.username} 
                  onChange={handleProfileChange} 
                  required 
                  disabled
                />
                <FormInput 
                  label="Email Address" 
                  type="email" 
                  name="email" 
                  value={profile.email} 
                  onChange={handleProfileChange} 
                  required 
                  disabled
                />
                <FormInput 
                  label="Real Name" 
                  type="text" 
                  name="real_name" 
                  value={profile.real_name} 
                  onChange={handleProfileChange} 
                />
                <FormInput 
                  label="Phone Number" 
                  type="text" 
                  name="phone_no" 
                  value={profile.phone_no} 
                  onChange={handleProfileChange} 
                />
                <FormInput 
                  label="Address" 
                  type="text" 
                  name="address" 
                  value={profile.address} 
                  onChange={handleProfileChange} 
                />
                <div className="pt-4">
                  <Button variant="primary" type="submit" loading={profileLoading} className="w-full justify-center text-sm">
                    Save Profile Details
                  </Button>
                </div>
              </form>
            </div>
          </div>

          {/* Change Password Panel */}
          <div className="bg-[#1E2437] p-6 rounded-xl border border-slate-800/80 shadow-lg flex flex-col justify-between">
            <div>
              <h3 className='text-lg font-bold mb-4 text-white border-b border-slate-850 pb-2'>Change Password</h3>
              <form onSubmit={handlePasswordSubmit} className='space-y-4'>
                <FormInput 
                  label="Old Password" 
                  type="password" 
                  name="oldPassword" 
                  value={password.oldPassword} 
                  onChange={handlePasswordChange} 
                  required 
                />
                <FormInput 
                  label="New Password" 
                  type="password" 
                  name="newPassword" 
                  value={password.newPassword} 
                  onChange={handlePasswordChange} 
                  required 
                />
                <FormInput 
                  label="Confirm Password" 
                  type="password" 
                  name="confirmPassword" 
                  value={password.confirmPassword} 
                  onChange={handlePasswordChange} 
                  required 
                />
                <div className="pt-4">
                  <Button variant="primary" type="submit" loading={passwordLoading} className="w-full justify-center text-sm">
                    Update Password
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChangePassword;
